import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SortingState } from '@tanstack/react-table';
import { Bell, RotateCcw, Send } from 'lucide-react';
import AppContext from 'contexts/AppContext';
import { Card } from 'components/ui/card';
import { cn } from 'lib/utils';
import { Skeleton } from 'components/ui/skeleton';
import { DataTable } from 'components/DataTable';
import EmptyState from 'components/EmptyState';
import ReferentialPageHeader from 'pages/AdminReferentials/components/ReferentialPageHeader';
import { IssuesServiceDeskSelect } from 'pages/AdminIssues/components/IssuesServiceDeskSelect';
import { useServiceDesks } from 'hooks/useServiceDesks';
import { useFollowUpRequests } from 'hooks/useInterventionRequests';
import { usePageCrumb } from 'components/admin/AppTopbar';
import { parseDeskAccess } from 'utils/deskAccess';
import { apiErrorMessage } from 'utils/errorMessage';
import {
  FOLLOW_UP_ORDER_BY,
  REFUSAL_REASON_CODES,
  REQUEST_STATUSES,
  followUpQuery,
  hasActiveFollowUpFilters,
  type FollowUpFilters,
  type FollowUpOrderBy,
  type FollowUpRequest,
  type InterventionRequestStatus,
} from 'api/interventionRequestsApi';
import { fetchFollowUpRequests } from 'api/interventionRequestsApi';
import { useToast } from 'components/ui/toast';
import { downloadBlob } from 'utils/downloadBlob';
import { getFollowUpColumns } from './components/columns';
import { FollowUpStats } from './components/FollowUpStats';
import { FollowUpFiltersBar } from './components/FollowUpFiltersBar';
import { FollowUpDrawer } from './components/FollowUpDrawer';
import { buildFollowUpCsv } from './csv';
import { followUpLoadError } from './vocabulary';

/**
 * « Demandes d'intervention » — what the back office sent, and what came back.
 *
 * **The name is the deliverable, not a detail.** The back office and the mobile app were about to
 * call the same object two things — « notification » here, « demande d'intervention » there — and
 * the e-mail already says the second. Naming the screen right on its first day is what closes that
 * risk; there is nothing to rename because nothing shipped under the other name.
 *
 * **Consultation only** (D36). Reassigning lives on the ticket screen, where the candidate ranking
 * is; every row links there rather than offering a second way to do the same thing. The mock-up's
 * « Relancer » only ever opened on `expired`, a status D5 deleted.
 */

/** The two fields the desk picker reads off the session — the shape `AdminIssues` declares too. */
type CurrentUser = { role?: string; serviceDeskId?: number | string | (number | string)[] };

const PAGE_SIZE = 20;

const BUTTON =
  'inline-flex h-[30px] items-center gap-1.5 rounded-lg border border-input bg-background px-3 text-[12.5px] font-medium text-foreground hover:bg-accent disabled:opacity-50';

/**
 * The export replays the **same filters** over the whole set rather than the page on screen — the
 * L2b pattern. Capped, and the cap is said out loud when it bites: a silently truncated file is
 * worse than no file, because nobody checks a spreadsheet's last row.
 */
const EXPORT_LIMIT = 1000;

/**
 * `pending` first and **oldest first** — the whole of D38. Sequential sending plus no expiry (D5)
 * means a request nobody answers stays stuck with no alert, so the view that opens is the one
 * showing what has been waiting longest. Seconds waited is `NOW() - sentAt`, so ascending on the
 * send *is* descending on the wait: one column, one sort.
 *
 * It is a **visible** default: it is written into the URL on arrival, so the filter bar shows it
 * and one click clears it. A default hidden in the schema would be a filter nobody can see.
 */
const DEFAULT_STATUS: InterventionRequestStatus[] = ['pending'];
const DEFAULT_SORT = { orderBy: 'sentAt' as FollowUpOrderBy, order: 'asc' as const };

const codes = <T,>(values: readonly T[], raw: string | null): T[] | undefined => {
  const parsed = (raw ?? '').split(',').filter((value) => values.includes(value as T)) as T[];
  return parsed.length ? Array.from(new Set(parsed)) : undefined;
};

const ids = (raw: string | null): number[] | undefined => {
  const parsed = (raw ?? '')
    .split(',')
    .map(Number)
    .filter((value) => Number.isInteger(value) && value > 0);
  return parsed.length ? Array.from(new Set(parsed)) : undefined;
};

/**
 * A **calendar** day, not merely a well-shaped one. `2026-02-31` passes the regex and `new Date`
 * does not throw on it — it rolls over to 3 March — so the round-trip is what catches it. Without
 * that, a hand-edited URL reached the backend's stricter schema and came back a 400, which is
 * exactly what this parser promises never to do.
 */
const day = (raw: string | null): string | undefined => {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return undefined;
  const parsed = new Date(`${raw}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== raw
    ? undefined
    : raw;
};

/**
 * The view, read back from the URL — and **every value validated**, so a hand-edited query string
 * degrades to the default view instead of a 400. The address bar is user input like any other.
 */
const parseFilters = (params: URLSearchParams, fallbackDeskId: number): FollowUpFilters => {
  const page = Number(params.get('page'));
  return {
    serviceDeskId: Number(params.get('serviceDeskId')) || fallbackDeskId,
    status: codes(REQUEST_STATUSES, params.get('status')),
    technicianId: ids(params.get('technicianId')),
    refusalReasonCode: codes(REFUSAL_REASON_CODES, params.get('refusalReasonCode')),
    outsidePerimeter: params.get('outsidePerimeter') === 'true' || undefined,
    search: params.get('search')?.trim() || undefined,
    sentFrom: day(params.get('sentFrom')),
    sentTo: day(params.get('sentTo')),
    interventionFrom: day(params.get('interventionFrom')),
    interventionTo: day(params.get('interventionTo')),
    orderBy:
      FOLLOW_UP_ORDER_BY.find((key) => key === params.get('orderBy')) ?? DEFAULT_SORT.orderBy,
    order: params.get('order') === 'desc' ? 'desc' : 'asc',
    page: Number.isInteger(page) && page > 0 ? page : 1,
    pageSize: PAGE_SIZE,
  };
};

const AdminInterventionRequests = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { currentUser } = useContext(AppContext) as { currentUser: CurrentUser | null };
  usePageCrumb(t('Intervention requests'));

  const [searchParams, setSearchParams] = useSearchParams();
  const { serviceDesks, isLoading: isLoadingServiceDesks, error: desksError } = useServiceDesks();

  // Mirrors the backend rule: superadmins and a '*' wildcard see every desk, everyone else only
  // the ids they are bound to. The desk is the picker, never a filter (D40).
  const deskAccess = useMemo(() => {
    const { all, ids: bound } = parseDeskAccess(currentUser?.serviceDeskId);
    return { all: currentUser?.role === 'superadmin' || all, ids: bound };
  }, [currentUser]);

  const deskOptions = useMemo(
    () =>
      serviceDesks
        .filter((desk: { enabled?: boolean }) => desk.enabled !== false)
        .filter((desk: { id: number }) => deskAccess.all || deskAccess.ids.has(desk.id))
        .map((desk: { id: number; name: string }) => ({ id: desk.id, label: desk.name })),
    [serviceDesks, deskAccess]
  );

  const [fallbackDeskId] = useMemo(() => Array.from(deskAccess.ids), [deskAccess]);
  const filters = useMemo(
    () => parseFilters(searchParams, fallbackDeskId ?? 0),
    [searchParams, fallbackDeskId]
  );

  /**
   * The view lives in the URL, not in `useState`: `App.js` mounts `<Routes key={pathname}>`, so
   * this subtree unmounts on every navigation and state would lose the filters on the way back
   * from a ticket — the ops loop itself. `replace` keeps typing out of the history.
   */
  const apply = useCallback(
    (next: FollowUpFilters) => setSearchParams(followUpQuery(next), { replace: true }),
    [setSearchParams]
  );

  // Page 1 in the **same** update: an effect would pair the new filters with the previous page,
  // and page 5 of a two-page result set renders an empty table that reads as "no results".
  /** The mission a `?open` link could not resolve — named in a notice until the next gesture. */
  const [openMissed, setOpenMissed] = useState<string | null>(null);
  const updateFilters = useCallback(
    (partial: Partial<FollowUpFilters>) => {
      setOpenMissed(null);
      apply({ ...filters, ...partial, page: 1 });
    },
    [apply, filters]
  );

  /**
   * Write the opening view into the URL, once, on an address that carries nothing. It is what
   * makes the default status a filter the operator can *see* and clear (D38) rather than a hidden
   * narrowing — and it snaps the desk to one this account may actually read.
   */
  useEffect(() => {
    if (!deskOptions.length) return;
    const deskIsValid = deskOptions.some((option) => option.id === filters.serviceDeskId);
    const seeded = searchParams.has('serviceDeskId');
    if (deskIsValid && seeded) return;
    apply({
      ...filters,
      ...(seeded ? {} : { status: DEFAULT_STATUS, ...DEFAULT_SORT }),
      serviceDeskId: deskIsValid ? filters.serviceDeskId : deskOptions[0].id,
      page: 1,
    });
  }, [deskOptions, filters, searchParams, apply]);

  const { data, isLoading, isFetching, error, refetch } = useFollowUpRequests(filters);
  /**
   * ⚠️ Three situations, and only one of them is a skeleton.
   *
   * `isLoading` is false while the query is disabled, so the skeleton needs the query's own
   * condition — without it the table renders its empty state during the desk resolution, which
   * reads as « aucune demande » on arrival. But that condition never clears when the desk list
   * **fails** or comes back empty, and a skeleton that spins forever is worse than either: it
   * never says what happened.
   */
  const resolvingDesk = filters.serviceDeskId === 0 && !desksError && isLoadingServiceDesks;
  const noReadableDesk = !isLoadingServiceDesks && !desksError && deskOptions.length === 0;
  // Memoised because the drawer's `opened` derives from it: a fresh `[]` on every render would
  // give that `useMemo` a new dependency each time and defeat it.
  const requests = useMemo(() => data?.requests ?? [], [data]);
  // Two different populations since D47: `total` counts the **missions** the table pages over,
  // `totalRequests` the requests behind them. The footer says both, or the page would look as if
  // requests had gone missing.
  const total = data?.total ?? 0;
  const totalRequests = data?.totalRequests ?? 0;

  const sorting = useMemo<SortingState>(
    () => [{ id: filters.orderBy, desc: filters.order === 'desc' }],
    [filters.orderBy, filters.order]
  );

  // A third click clears the sort; a server list still needs one, so it falls back to the default.
  const onSortingChange = (next: SortingState) => {
    const [column] = next;
    updateFilters(
      column
        ? { orderBy: column.id as FollowUpOrderBy, order: column.desc ? 'desc' : 'asc' }
        : DEFAULT_SORT
    );
  };

  const [exporting, setExporting] = useState(false);
  /**
   * The **id**, never the row: the row is refetched under the drawer on every invalidation, and
   * holding the object would keep a stale copy open — the trap the ticket screen's drawer names.
   */
  const [openedId, setOpenedId] = useState<number | null>(null);
  const openDrawer = (id: number) => {
    setOpenMissed(null);
    setOpenedId(id);
  };
  const opened = useMemo(
    () => requests.find((request) => request.id === openedId) ?? null,
    [requests, openedId]
  );

  /**
   * `?open=<issueKey>` (the bell, CA19): taken off the URL at once, so closing the drawer does not
   * reopen it, then resolved against the first settled page — the drawer, or an honest notice.
   * A failed read drops it silently: the error banner already speaks, the notice would guess.
   */
  const [pendingOpen, setPendingOpen] = useState<string | null>(null);
  const openKey = searchParams.get('open');
  useEffect(() => {
    if (!openKey) return;
    setPendingOpen(openKey);
    setOpenMissed(null);
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    setSearchParams(next, { replace: true });
  }, [openKey, searchParams, setSearchParams]);
  useEffect(() => {
    if (!pendingOpen) return;
    if (desksError || error) {
      setPendingOpen(null);
      return;
    }
    if (!noReadableDesk && (!data || isFetching)) return;
    const row = requests.find((request) => request.issueKey === pendingOpen);
    setPendingOpen(null);
    if (row) setOpenedId(row.id);
    else setOpenMissed(pendingOpen);
  }, [pendingOpen, noReadableDesk, desksError, error, data, isFetching, requests]);

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const full = await fetchFollowUpRequests({ ...filters, page: 1, pageSize: EXPORT_LIMIT });
      // Said out loud when it bites: a silently truncated file is worse than no file, because
      // nobody checks a spreadsheet's last row. The same sentence the directory export uses.
      if (full.total > full.requests.length) {
        toast.error(
          t('Only the first {{count}} rows were exported. Narrow the filters to get the rest.', {
            count: full.requests.length,
          })
        );
      }
      downloadBlob(
        new Blob([buildFollowUpCsv(t, full.requests)], { type: 'text/csv;charset=utf-8;' }),
        `intervention-requests-${new Date().toISOString().slice(0, 10)}.csv`
      );
    } catch (exportError) {
      toast.error(apiErrorMessage(t, exportError));
    } finally {
      setExporting(false);
    }
  };

  // Clears what was narrowed, not the sort nor the desk: neither is a filter.
  const resetFilters = () =>
    apply({
      serviceDeskId: filters.serviceDeskId,
      orderBy: filters.orderBy,
      order: filters.order,
      page: 1,
      pageSize: PAGE_SIZE,
    });

  const jiraStatusAvailable = data?.jiraStatusAvailable ?? false;
  const columns = useMemo(
    () => getFollowUpColumns({ t, jiraStatusAvailable }),
    [t, jiraStatusAvailable]
  );
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="container mx-auto p-6">
      {/* Each block owns its own bottom margin. `space-y-4` used to do it for them, which wrote
          `margin-bottom: 0` onto the stat grid and silently killed the 26px the design asks for. */}
      <div className="mb-4">
        <ReferentialPageHeader
          icon={Send}
          title={t('Intervention requests')}
          subtitle={t(
            'One row per mission. Several technicians can be solicited in parallel: the first to accept takes the mission, the others are superseded.'
          )}
        />
      </div>

      {/* Only once the figures are known: four zeros under a failed read would be a claim. */}
      {data?.stats && (
        <FollowUpStats
          counts={data.stats}
          selected={filters.status ?? []}
          /* The page total counts the same population as the cards only while no status narrows
             it — the cards are computed without that filter, so under one the figure is not ours
             to claim. */
          totalMissions={filters.status?.length ? undefined : total}
          onSelect={(status) => updateFilters({ status: status ? [status] : undefined })}
          sentFrom={filters.sentFrom}
          sentTo={filters.sentTo}
        />
      )}

      <Card className="rounded-[14px]">
        {openMissed && (
          <p role="status" className="border-b px-5 py-3 text-[13px] text-amber-800 dark:text-amber-300">
            {t('{{issueKey}} could not be opened: it is not in this list, or its service desk is not one you can read.', { issueKey: openMissed })}
          </p>
        )}
        <div className="border-b px-5 py-4">
          <FollowUpFiltersBar
            filters={filters}
            onChange={updateFilters}
            onReset={resetFilters}
            onExport={exportCsv}
            exporting={exporting}
            canExport={total > 0}
            deskSelect={
              <IssuesServiceDeskSelect
                value={filters.serviceDeskId}
                onChange={(serviceDeskId) => updateFilters({ serviceDeskId })}
                options={deskOptions}
                isLoading={isLoadingServiceDesks}
              />
            }
          />
        </div>

        {desksError ? (
          <p role="alert" className="p-5 text-sm text-red-600">
            {apiErrorMessage(t, desksError)}
          </p>
        ) : noReadableDesk ? (
          // Not an empty table: nothing was asked, because there is nothing this account may read.
          <div className="p-5">
            <EmptyState title={t('No service desk you can read')} />
          </div>
        ) : isLoading || resolvingDesk ? (
          <div className="space-y-2 p-5">
            {[0, 1, 2, 3, 4].map((row) => (
              <Skeleton key={row} className="h-10 w-full" />
            ))}
          </div>
        ) : error ? (
          // An alert, **never an empty table**: a table with no rows under a failed load reads as
          // "nothing was sent", which is the defect #415 has just fixed on the ticket screen.
          // And a way back: a transient failure must not cost a page reload.
          <div role="alert" className="flex flex-col items-center gap-3 px-5 py-[41px] text-center">
            <div>
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                {t('Unable to load the requests')}
              </p>
              <p className="mt-[5px] text-[13px] text-muted-foreground">
                {followUpLoadError(t, error)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className={BUTTON}
            >
              <RotateCcw className="h-[14px] w-[14px]" aria-hidden="true" />
              {t('Retry')}
            </button>
          </div>
        ) : (
          /* The header and cell metrics of the design (14 px), over `embedded`'s shared 9 px —
             the extra `table` in the selector is what outranks it without touching DataTable. */
          <div className="[&_table_td:first-child]:pl-5 [&_table_td:last-child]:pr-5 [&_table_td]:px-[14px] [&_table_th:first-child]:pl-5 [&_table_th:last-child]:pr-5 [&_table_th]:select-none [&_table_th]:whitespace-nowrap [&_table_th]:px-[14px] [&_thead_tr]:border-b-0 [&_tbody_tr]:border-b-0 [&_tbody_tr]:border-t [&_tbody_tr:last-child]:border-t">
            <DataTable
              embedded
              data={requests}
              columns={columns}
              // Both are client-side in DataTable, so both would act on one page only.
              showGlobalSearch={false}
              showExport={false}
              serverSorting={{ sorting, onSortingChange }}
              serverPagination={{
                pageIndex: filters.page - 1,
                pageCount,
                totalCount: total,
                hasNextPage: filters.page < pageCount,
                busy: isFetching,
                // Dot-separated fragments, each pluralised on its own count: the design's own
                // reading, and « 1 missions » cannot happen.
                summary: total === 0 ? undefined : [
                  t('Page {{page}}', { page: filters.page }),
                  t('{{count}} mission', { count: requests.length }),
                  t('{{count}} request', { count: totalRequests }),
                  t('{{count}} mission in total', { count: total }),
                ].join(' · '),
                onPreviousPage: () => apply({ ...filters, page: filters.page - 1 }),
                onNextPage: () => apply({ ...filters, page: filters.page + 1 }),
              }}
              // The row opens the drawer; the « Ouvrir le ticket » link owns its own click, which
              // DataTable's guard keeps out of this handler.
              onRowClick={(row: FollowUpRequest) => openDrawer(row.id)}
              // The row the drawer is open on, marked on the row itself rather than by an accent
              // column the design does not have.
              rowClassName={(row: FollowUpRequest) => (row.id === openedId ? 'bg-muted/60' : '')}
              emptyMessage={
                // 41 and not the design's 54: the empty row is a `td` like any other and already
                // carries the table's own 13px, which the two add up to.
                <div className="py-[41px]">
                  {/* Two vides, never one: "nothing has been sent" and "this filter finds nothing"
                      are different facts, and the second is the operator's own doing. */}
                  {hasActiveFollowUpFilters(filters) ? (
                    <div className="flex flex-col items-center">
                      <EmptyState icon={Bell} title={t('No request matches — adjust your filters')} />
                      <button type="button" onClick={resetFilters} className={cn(BUTTON, '-mt-8')}>
                        {t('Reset filters')}
                      </button>
                    </div>
                  ) : (
                    <EmptyState
                      icon={Bell}
                      title={t('No intervention request sent')}
                      hint={t('Solicitations are made from the Tickets screen.')}
                    />
                  )}
                </div>
              }
            />
          </div>
        )}
      </Card>

      <FollowUpDrawer
        request={opened}
        jiraStatusAvailable={jiraStatusAvailable}
        deadlineEditable={serviceDesks.some(
          (desk: { id: number; hasInterventionQualification?: boolean }) =>
            desk.id === opened?.serviceDeskId && desk.hasInterventionQualification === true
        )}
        onClose={() => setOpenedId(null)}
      />
    </div>
  );
};

export default AdminInterventionRequests;
